"use client";

/**
 * AdvisorDesk — "supervised triage" panel from the hero-showcase concept:
 * a KPI bar plus two live lists (calls in progress, items escalated to the
 * advisor). Explicitly labeled "ILLUSTRATIVE · a typical afternoon" with
 * capped timers — this is a mocked demo of what the desk will look like,
 * not a live read of real traffic. Becomes genuinely live later via a Step
 * Function read-API (ListExecutions/DescribeExecution) + escalation agents
 * — that's roadmap, not this page.
 *
 * Numbers below were reconciled to one coherent story (the reference
 * concept had a 9/14/31 mismatch — answered exceeded total conversations,
 * which doesn't add up). Here: 44 conversations today = 39 answered by the
 * machine + 2 escalated to the advisor + 3 still on the line (in progress,
 * not yet in either bucket) — the two lists below show exactly those 2 and
 * 3 items, so the numbers on screen always match what's rendered.
 */

import { useEffect, useRef } from "react";

const TOTAL_CONVERSATIONS = 44;
const ANSWERED_BY_MACHINE = 39;

const CALLS = [
  { n: "Margaret L", v: "the line", q: "How did my income sleeve hold up?" },
  { n: "Robert C", v: "the line", q: "What's my correlation to the S&P now?" },
  { n: "David & Sue P", v: "chat", q: "Did we beat the market this quarter?" },
];

const ESCALATIONS = [
  {
    q: "“Should we revisit my estate plan now that Dad's trust settled?”",
    why:
      "Needed judgment — outside what she's allowed to answer. She's built to escalate here, not guess.",
    who: "Rachel H · via the line · just now",
  },
  {
    q: "“I'm worried how concentrated we are in Apple. Can we talk it through?”",
    why:
      "The numbers were answered (18% of book, computed) — but reassurance is advice, and advice is yours to give, not hers.",
    who: "Tom B · via chat · just now",
  },
];

function fmt(s: number) {
  const m = Math.floor(s / 60);
  const ss = s % 60;
  return `${m}:${ss < 10 ? "0" : ""}${ss}`;
}

export function AdvisorDesk() {
  const rootRef = useRef<HTMLDivElement>(null);
  const startedRef = useRef(false);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)")
      .matches;
    const $ = <T extends HTMLElement>(id: string) => root.querySelector<T>(`#${id}`);

    const timers: ReturnType<typeof setTimeout | typeof setInterval>[] = [];

    function countTo(el: HTMLElement | null, target: number, ms: number) {
      if (!el) return;
      if (reduce) {
        el.textContent = String(target);
        return;
      }
      let t0: number | null = null;
      const step = (ts: number) => {
        if (t0 === null) t0 = ts;
        const p = Math.min((ts - t0) / ms, 1);
        el.textContent = String(Math.round(p * target));
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }

    function makeCall(c: (typeof CALLS)[number], secs: number) {
      const d = document.createElement("div");
      d.className = "callrow";
      d.innerHTML =
        '<div class="lwave"><i></i><i></i><i></i><i></i></div>' +
        `<div class="cwho"><b>${c.n}</b><small>${c.q} · via ${c.v}</small></div>` +
        `<div class="ctimer" data-s="${secs}">${fmt(secs)}</div>` +
        '<div class="clisten">Listen</div>';
      return d;
    }
    function makeEsc(e: (typeof ESCALATIONS)[number]) {
      const d = document.createElement("div");
      d.className = "esccard";
      d.innerHTML =
        `<div class="q">${e.q}</div><div class="why">${e.why}</div>` +
        `<div class="row"><span class="who">${e.who}</span><span class="join">Join</span></div>`;
      return d;
    }

    function fire() {
      if (startedRef.current) return;
      startedRef.current = true;

      countTo($("dConv"), TOTAL_CONVERSATIONS, 900);
      countTo($("dAns"), ANSWERED_BY_MACHINE, 1200);

      const liveList = $("liveList");
      const escList = $("escList");
      if (!liveList || !escList) return;

      if (reduce) {
        CALLS.forEach((c, i) => {
          const d = makeCall(c, [142, 86, 25][i]);
          d.classList.add("on");
          liveList.appendChild(d);
        });
        ESCALATIONS.forEach((e) => {
          const d = makeEsc(e);
          d.classList.add("on");
          escList.appendChild(d);
        });
        const liveCount = $("liveCount");
        const escCount = $("escCount");
        const clock = $("dClock");
        if (liveCount) liveCount.textContent = String(CALLS.length);
        if (escCount) escCount.textContent = String(ESCALATIONS.length);
        countTo($("dEsc"), ESCALATIONS.length, 0);
        if (clock) clock.textContent = "3:14 PM";
        return;
      }

      // Seed live calls, staggered.
      const seed = [142, 86, 25];
      CALLS.forEach((c, i) => {
        const t = setTimeout(() => {
          const d = makeCall(c, seed[i]);
          liveList.appendChild(d);
          requestAnimationFrame(() => d.classList.add("on"));
          const liveCount = $("liveCount");
          if (liveCount) {
            liveCount.textContent = String(
              liveList.querySelectorAll(".callrow").length
            );
          }
        }, 400 + i * 550);
        timers.push(t);
      });

      // Escalations flash in.
      ESCALATIONS.forEach((e, i) => {
        const t = setTimeout(() => {
          const d = makeEsc(e);
          escList.appendChild(d);
          requestAnimationFrame(() => {
            d.classList.add("on");
            d.classList.add("fresh");
          });
          const escCount = $("escCount");
          if (escCount) {
            escCount.textContent = String(escList.children.length);
          }
          countTo($("dEsc"), escList.children.length, 250);
        }, 1400 + i * 900);
        timers.push(t);
      });

      // Running timers on live calls — capped at 9:59 so an "always-on"
      // demo call never looks like a multi-hour hostage situation.
      const tickTimer = setInterval(() => {
        rootRef.current?.querySelectorAll<HTMLElement>("#liveList .ctimer").forEach((t) => {
          const s = parseInt(t.getAttribute("data-s") || "0", 10);
          if (s < 599) {
            t.setAttribute("data-s", String(s + 1));
            t.textContent = fmt(s + 1);
          }
        });
      }, 1000);
      timers.push(tickTimer);

      // A wall clock, capped so it doesn't run all day in a background tab.
      let base = 3 * 3600 + 14 * 60;
      let ticks = 0;
      const clockTimer = setInterval(() => {
        base++;
        ticks++;
        const h = Math.floor(base / 3600) % 12 || 12;
        const m = Math.floor(base / 60) % 60;
        const clock = $("dClock");
        if (clock) clock.textContent = `${h}:${m < 10 ? "0" : ""}${m} PM`;
        if (ticks > 120) clearInterval(clockTimer);
      }, 1000);
      timers.push(clockTimer);
      const clock = $("dClock");
      if (clock) clock.textContent = "3:14 PM";
    }

    if (reduce || !("IntersectionObserver" in window)) {
      fire();
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            fire();
            io.disconnect();
          }
        });
      },
      { threshold: 0.25 }
    );
    io.observe(root);

    return () => {
      io.disconnect();
      timers.forEach((t) => clearTimeout(t as ReturnType<typeof setTimeout>));
    };
  }, []);

  return (
    <div className="desk" id="desk" ref={rootRef}>
      <div className="lbl">Supervised triage — the advisor&rsquo;s desk</div>
      <h2>The line answers what it can prove. You get what needs you.</h2>
      <p className="sub">
        A representative afternoon. In production this reads live from your
        pipeline — every run, every escalation, every sign-off. Here is what
        reaches the advisor.
      </p>
      <div className="deskpanel">
        <div className="deskbar">
          <span className="livedot">ILLUSTRATIVE · a typical afternoon</span>
          <span>
            Today <b id="dConv">0</b> conversations
          </span>
          <span className="ok">
            <b id="dAns">0</b> answered · computed &amp; cited
          </span>
          <span className="esc">
            <b id="dEsc">0</b> need you
          </span>
          <span className="clock" id="dClock">
            —
          </span>
        </div>
        <div className="dashgrid">
          <div className="dashcol">
            <div className="colhd">
              On the line now <span id="liveCount">0</span>
            </div>
            <div id="liveList" />
          </div>
          <div className="dashcol">
            <div className="colhd esc">
              Escalated to you <span id="escCount">0</span>
            </div>
            <div id="escList" />
          </div>
        </div>
        <div className="deskfoot">
          <span>
            <b>Every conversation logged</b> — who asked, what was answered,
            who joined — books-and-records ready.
          </span>
          <span>Watch any call live · step in with one tap.</span>
        </div>
      </div>
    </div>
  );
}
